import React, { useState } from 'react';
import { PrimaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface SlotMachineGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const REEL_MAP: Record<string, string[]> = {
  'Jackpot! Free Meal': ['7️⃣', '7️⃣', '7️⃣'],
  '20% OFF': ['🍒', '🍒', '🍋'],
  'No Match': ['🍋', '🍇', '🍒'],
};
const SYMBOLS = ['🍒', '🍋', '🍇', '7️⃣', '🔔'];

const SlotMachineGame: React.FC<SlotMachineGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'SLOT_MACHINE');
  const [reels, setReels] = useState<string[]>(['🍒', '🍒', '🍒']);
  const [spinning, setSpinning] = useState(false);
  const [played, setPlayed] = useState(false);
  const [resultLabel, setResultLabel] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState<string | null>(null);

  async function handlePull() {
    if (spinning || played || !eligible) return;
    setSpinning(true);
    setResultLabel(null);
    const spinInterval = setInterval(() => {
      setReels([0, 0, 0].map(() => SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]));
    }, 90);
    try {
      const res = await gamesService.spin(
        appName,
        'SLOT_MACHINE',
        gamesService.dailyIdempotencyKey(outletId, 'SLOT_MACHINE')
      );
      await new Promise((r) => setTimeout(r, 1400));
      clearInterval(spinInterval);
      setReels(REEL_MAP[res.resultSegment] ?? ['🍋', '🍇', '🍒']);
      setResultLabel(res.resultSegment);
      setRedemptionCode(res.resultSegment !== 'No Match' ? res.redemptionCode : null);
      setPlayed(true);
    } catch {
      clearInterval(spinInterval);
      setReels(['🍋', '🍇', '🍒']);
      setResultLabel('Come back tomorrow for another spin.');
      setPlayed(true);
    }
    setSpinning(false);
  }

  const locked = !loading && !eligible;

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Lucky <span style={{ color: '#D9472B' }}>Slots</span></>}
        subtitle="Match 3 symbols for the jackpot"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
            <div style={{ display: 'flex', gap: 8, margin: '10px 0 18px' }}>
              {reels.map((sym, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: 84,
                    background: '#FFFFFF',
                    border: '2px solid var(--primary, #F5C21A)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 34,
                    boxShadow: '0 1px 6px rgba(0,0,0,.06)',
                  }}
                >
                  {sym}
                </div>
              ))}
            </div>

            {resultLabel && !spinning && (
              <>
                <Card style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--primary-dark, #D4A416)' }}>
                    {resultLabel}
                  </div>
                  {redemptionCode && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>CODE</div>
                      <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: 2 }}>{redemptionCode}</div>
                    </div>
                  )}
                </Card>
                <div style={{ height: 14 }} />
              </>
            )}

            <PrimaryButton onClick={handlePull} disabled={spinning || played}>
              {spinning ? 'SPINNING…' : played ? 'COME BACK TOMORROW' : 'PULL LEVER'}
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};

export default SlotMachineGame;
